import { baseApi } from '@/shared/api/baseApi.js';
import type { Project, ProjectWithRooms } from '@prostostroy/shared';
import type { ProjectCreateRequest, ProjectUpdateRequest } from './projects.types.js';

export interface ProjectPhoto {
  id: string;
  projectId: string;
  url: string;
  thumbnailUrl: string | null;
  fileName: string;
  fileSize: number;
  mimeType: string;
  isCover: boolean;
  createdAt: string;
  uploadedByUserId: string;
  uploadedByName: string;
}

export interface ProjectFile {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
  uploadedByName: string | null;
}

export const projectsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<{ success: true; data: Project[] }, void>({
      query: () => '/projects',
      providesTags: ['Projects'],
    }),

    getEarningsSummary: builder.query<
      {
        success: true;
        data: { totalEarnings: string; totalTurnover: string; completedCount: number };
      },
      void
    >({
      query: () => '/projects/earnings-summary',
      providesTags: ['Projects', 'Estimates'],
    }),

    getProject: builder.query<{ success: true; data: ProjectWithRooms }, string>({
      query: (id) => `/projects/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Project', id }],
    }),

    createProject: builder.mutation<{ success: true; data: Project }, ProjectCreateRequest>({
      query: (body) => ({ url: '/projects', method: 'POST', body }),
      invalidatesTags: ['Projects'],
    }),

    updateProject: builder.mutation<
      { success: true; data: Project },
      { id: string; data: ProjectUpdateRequest }
    >({
      query: ({ id, data }) => ({ url: `/projects/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { id }) => [
        'Projects',
        { type: 'Project', id },
        { type: 'ProjectActivity', id },
        'Conversations',
      ],
    }),

    deleteProject: builder.mutation<void, string>({
      query: (id) => ({ url: `/projects/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Projects', 'Conversations'],
    }),

    duplicateProject: builder.mutation<
      { success: true; data: Project },
      { id: string; name?: string }
    >({
      query: ({ id, name }) => ({
        url: `/projects/${id}/duplicate`,
        method: 'POST',
        body: name ? { name } : {},
      }),
      invalidatesTags: ['Projects'],
    }),

    getProjectPermissions: builder.query<
      {
        success: true;
        data: {
          isOwner: boolean;
          canViewRooms: boolean;
          canViewRates: boolean;
          canViewEstimate: boolean;
          canViewTasks: boolean;
          canViewSettings: boolean;
          canViewMembers: boolean;
          canEditMembers: boolean;
          canEditGeneral: boolean;
          canAccessChat: boolean;
          canEditChatParticipants: boolean;
          canManagePermissions: boolean;
          tasksScope: string;
        };
      },
      string
    >({
      query: (projectId) => `/projects/${projectId}/permissions`,
      providesTags: (_r, _e, id) => [{ type: 'Project', id }],
    }),

    getProjectMembers: builder.query<{ success: true; data: any[] }, string>({
      query: (projectId) => `/projects/${projectId}/members`,
      providesTags: (_r, _e, id) => [{ type: 'Project', id }],
    }),

    addProjectMember: builder.mutation<
      { success: true; data: any },
      {
        projectId: string;
        userId: string;
        role?: 'lead' | 'worker';
        specialization?: string;
        addToChat?: boolean;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Project', id: projectId },
        'Teams',
        'Conversations',
      ],
    }),

    removeProjectMember: builder.mutation<
      { success: true; data: any },
      { projectId: string; memberId: string }
    >({
      query: ({ projectId, memberId }) => ({
        url: `/projects/${projectId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'Project', id: projectId }, 'Teams'],
    }),

    getProjectPhotos: builder.query<{ success: true; data: ProjectPhoto[] }, string>({
      query: (projectId) => `/projects/${projectId}/photos`,
      providesTags: (_r, _e, id) => [{ type: 'ProjectPhotos' as const, id }],
    }),

    uploadProjectPhoto: builder.mutation<
      { success: true; data: ProjectPhoto },
      { projectId: string; formData: FormData }
    >({
      query: ({ projectId, formData }) => ({
        url: `/projects/${projectId}/photos`,
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'ProjectPhotos' as const, id: projectId },
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    deleteProjectPhoto: builder.mutation<
      { success: true; data: { id: string } },
      { projectId: string; photoId: string }
    >({
      query: ({ projectId, photoId }) => ({
        url: `/projects/${projectId}/photos/${photoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'ProjectPhotos' as const, id: projectId },
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    setProjectCoverPhoto: builder.mutation<
      { success: true; data: { id: string } },
      { projectId: string; photoId: string }
    >({
      query: ({ projectId, photoId }) => ({
        url: `/projects/${projectId}/photos/${photoId}/cover`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'ProjectPhotos' as const, id: projectId },
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    getProjectFiles: builder.query<{ success: true; data: ProjectFile[] }, string>({
      query: (projectId) => `/projects/${projectId}/files`,
      providesTags: (_r, _e, id) => [{ type: 'ProjectFiles' as const, id }],
    }),

    uploadProjectFile: builder.mutation<
      { success: true; data: ProjectFile },
      { projectId: string; formData: FormData }
    >({
      query: ({ projectId, formData }) => ({
        url: `/projects/${projectId}/files`,
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'ProjectFiles' as const, id: projectId },
      ],
    }),

    deleteProjectFile: builder.mutation<{ success: true }, { projectId: string; fileId: string }>({
      query: ({ projectId, fileId }) => ({
        url: `/projects/${projectId}/files/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'ProjectFiles' as const, id: projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetEarningsSummaryQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useDuplicateProjectMutation,
  useGetProjectPermissionsQuery,
  useGetProjectMembersQuery,
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
  useGetProjectPhotosQuery,
  useUploadProjectPhotoMutation,
  useDeleteProjectPhotoMutation,
  useSetProjectCoverPhotoMutation,
  useGetProjectFilesQuery,
  useUploadProjectFileMutation,
  useDeleteProjectFileMutation,
} = projectsApi;
